import { zodResolver } from '@hookform/resolvers/zod'
import { LoadingButton } from '@mui/lab'
import { Alert, Box, Button, Stack } from '@mui/material'
import { createTeamMember, teamMemberCreatedMessage } from 'api/teams'
import { useAuthContext } from 'components/context/AuthProvider'
import { getFamilySeatsLeft } from 'features/Subscription/utils'
import {
  CaregiverRoleFields,
  combineRoleValues,
} from 'components/form-elements/CaregiverRoleFields'
import FormInput from 'components/form-elements/FormInput'
import NotificationSys from 'components/NotificationSystem'
import { EMAIL_IS_INVALID, minButtonWidth, PHONE_IS_INVALID } from 'const'
import { useCharactersLeft } from 'hooks/useCharactersLeft'
import React, { useMemo } from 'react'
import { FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useMutation } from 'react-query'
import { TeamMemberCreateRequest, TraineeTeamRole } from 'types'
import { array, literal, number, object, string, TypeOf, union } from 'zod'

const required = string().min(1, 'Required')
const optional = string().optional()

const prepaidLicensesMessage = (count: number) => {
  if (count === 0) return 'There are no prepaid licenses available.'
  if (count === 1) return 'There is 1 prepaid license available.'
  return `There are ${count} prepaid licenses available.`
}

interface TimelineTeamMemberFormAddProps {
  onClose: () => void
  teamId: number
  onRefetchTeamMembers: () => Promise<void>
}

export function TimelineCaregiverTeamMemberFormAdd({
  onClose,
  teamId,
  onRefetchTeamMembers,
}: TimelineTeamMemberFormAddProps) {
  const formSchema = object({
    memberSelect: union([number(), string().transform((val) => parseInt(val, 10))])
      .optional()
      .nullable(),
    firstName: required,
    lastName: optional,
    email: string().email(EMAIL_IS_INVALID).optional().or(literal('')),
    about: string().max(200, 'Max message length is 200 characters').optional(),
    professionalRole: array(
      object({
        label: string(),
        value: string(),
      }),
    ).optional(),
    familyRole: array(
      object({
        label: string(),
        value: string(),
      }),
    ).optional(),
    otherRole: optional,
    addressStreet: optional,
    addressZipCode: optional,
    addressCountry: optional,
    addressCity: optional,
    addressState: optional,
    phone: string().min(10, PHONE_IS_INVALID).or(literal('')).optional(),
    comments: string(),
  })

  type FormType = TypeOf<typeof formSchema>

  const methods = useForm<FormType>({
    mode: 'onChange',
    resolver: zodResolver(formSchema),
    defaultValues: {
      memberSelect: undefined,
      firstName: '',
      lastName: '',
      email: '',
      about: '',
      professionalRole: [],
      familyRole: [],
      addressStreet: '',
      addressZipCode: '',
      addressCountry: '',
      addressCity: '',
      addressState: '',
      phone: '',
      comments: '',
    },
  })
  const { handleSubmit, watch } = methods

  // A Family owner's invite reserves a seat, so the invitee needs no subscription of their own.
  const { user } = useAuthContext()
  const prepaidLicensesAvailable = getFamilySeatsLeft(user?.familyContext)

  const memberSelectValue = useWatch({ name: 'memberSelect', control: methods.control })

  const createMutation = useMutation(createTeamMember, {
    onSuccess: async (response) => {
      NotificationSys.showSuccess(teamMemberCreatedMessage(response.data?.inviteSentVia))
      onClose()
      await onRefetchTeamMembers()
    },
  })

  const onSubmitHandler: SubmitHandler<FormType> = (values: FormType) => {
    if (!teamId) {
      NotificationSys.showWarning('No team selected')
      return
    }

    const role = combineRoleValues(values.professionalRole, values.familyRole)
    const otherRole = role.includes(TraineeTeamRole.other) ? values.otherRole || '' : ''

    const userId = values.memberSelect ?? 0

    const request: TeamMemberCreateRequest = {
      id: teamId,
      userId,
      email: values.email || '',
      firstName: values.firstName,
      lastName: values.lastName || '',
      about: values.about || '',
      role,
      otherRole,
      street: values.addressStreet || '',
      zipCode: values.addressZipCode || '',
      country: values.addressCountry || '',
      city: values.addressCity || '',
      state: values.addressState || '',
      phone: values.phone || '',
      comments: values.comments || '',
    }

    createMutation.mutate(request)
  }

  const aboutMeValue = watch('about')
  const charactersLeft = useCharactersLeft(200, aboutMeValue?.length || 0)

  const hasActiveMember = useMemo(
    () => memberSelectValue !== null && memberSelectValue !== undefined,
    [memberSelectValue],
  )

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={handleSubmit(onSubmitHandler)} width="100%" noValidate>
        <Stack spacing={1.5} mb={2}>
          <Alert severity={prepaidLicensesAvailable > 0 ? 'info' : 'warning'}>
            {prepaidLicensesMessage(prepaidLicensesAvailable)}
          </Alert>

          <FormInput label="First Name" name="firstName" fullWidth disabled={hasActiveMember} />

          <FormInput label="Last Name" name="lastName" fullWidth disabled={hasActiveMember} />

          <FormInput
            label="Email"
            name="email"
            fullWidth
            disabled={hasActiveMember}
            helperText="Add an email to invite this person to join your care team."
          />

          <CaregiverRoleFields />

          <FormInput label="Mobile Phone Number" name="phone" fullWidth />

          <FormInput label="Street Address" name="addressStreet" fullWidth />
          <FormInput label="Zip Code" name="addressZipCode" fullWidth />
          <FormInput label="City" name="addressCity" fullWidth />
          <FormInput label="State" name="addressState" fullWidth />
          <FormInput label="Country" name="addressCountry" fullWidth />

          <FormInput
            name="about"
            label="What you might want to know about me"
            placeholder="Please enter the relevant information about you"
            fullWidth
            minRows={3}
            maxRows={6}
            multiline
            helperText={charactersLeft}
            checkTouchField={false}
            disabled={hasActiveMember}
          />

          <FormInput label="Comments" name="comments" fullWidth multiline />
        </Stack>

        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
          <Button variant="outlined" onClick={onClose} sx={minButtonWidth}>
            Close
          </Button>
          <LoadingButton
            loading={createMutation.isLoading}
            variant="contained"
            type="submit"
            sx={minButtonWidth}
          >
            Save
          </LoadingButton>
        </Stack>
      </Box>
    </FormProvider>
  )
}
